import { useState, useEffect, memo } from 'react'
import classnames from 'classnames'
import Logo from '../../logo'
import { useAuth } from '../../../contexts/auth'
import { ROUTES, zIndex } from '../../../constants'
import UserMenu from '../../user-menu'
import { VIEWS } from '../../../containers/auth/modals'
import FlatX from '../../svg/flat-x'
import Hamburger from '../../svg/hamburger'
import { Button } from '../../button'
import useNotifications from '../../../hooks/use-notifications'
import { Desktop, Mobile } from '../../media'
import { useMediaQuery } from 'react-responsive'
import { NotificationType } from '../../../api-client/enums'
import Message from '../../svg/message'
import { useCallback } from 'react'
import Link from 'next/link'
import Router, { useRouter } from 'next/router'
import { useRef } from 'react'

const Header = memo(() => {
  const [open, setOpen] = useState(false)
  const [{ isAuthenticated, userDetails }, { setAuthModal, logout }] = useAuth()
  const { isNotifications, refetchNotifications } = useNotifications()
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const { notifications } = useNotifications()
  const [chatCount, setChatCount] = useState(0)
  const router = useRouter()

  // for home page
  const isHome = router.pathname === '/' || router.pathname === '/en'
  const [scrolled, setScrolled] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreRef = useRef(null)

  const onRouteChangeComplete = () => {
    setOpen(false)
    setMoreOpen(false)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setMoreOpen(false)
      }
    }

    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [moreOpen])

  useEffect(() => {
    if (!isHome) return

    const onScroll = () => {
      setScrolled(window.scrollY > 40)
    }

    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [isHome])

  useEffect(() => {
    const onRouteChangeComplete = () => setOpen(false)

    Router.events.on('routeChangeComplete', onRouteChangeComplete)

    return () => Router.events.off('routeChangeComplete', onRouteChangeComplete)
  }, [])

  useEffect(() => {
    // close mobile menu if resizing
    if (open && !isMobile) {
      setOpen(false)
    }
  }, [open, isMobile])

  const handleMessageClick = useCallback(() => {
    setOpen(false)
    const timeoutRef = setTimeout(() => {
      Router.push(`${ROUTES.Account}#chatHistory`)
      clearTimeout(timeoutRef)
    }, 300)
  }, [])

  useEffect(() => {
    setChatCount(
      new Set(notifications[NotificationType.Message]?.map((chat) => chat.conversationId)).size,
    )
  }, [notifications])

  useEffect(() => {
    refetchNotifications()
  }, [router.asPath]) //eslint-disable-line
  // not including refetchNotifications here since it would trigger an infinite loop

  return (
    // for home page
    <header className={classnames({ open, homeHeader: isHome, scrolled: isHome && scrolled })}>
      <div className='container flex justifyBetween alignCenter'>
        <div className='logoContainer'>
          <Logo />
        </div>
        <nav className='mainNavigation'>
          <ul className='flex alignCenter'>
            <li>
              <Link legacyBehavior href={`${ROUTES.Buy}`}>
                <a>Buy</a>
              </Link>
            </li>
            <li>
              <Link legacyBehavior href={ROUTES.Sell}>
                <a>Sell</a>
              </Link>
            </li>
            <li>
              <Link legacyBehavior href={ROUTES.Search}>
                <a>Search</a>
              </Link>
            </li>
            {(userDetails.emailConfirmed || userDetails.verified) && (
              <li>
                <Link legacyBehavior href={ROUTES.Account}>
                  <a className='flex alignCenter'>
                    My Vehicles
                    {isNotifications &&
                      (notifications[NotificationType.Bidding] ||
                        notifications[NotificationType.Vehicle] ||
                        notifications[NotificationType.Favorite]) && (
                        <Mobile>
                          <div className='notifications' aria-label='active notification' />
                        </Mobile>
                      )}
                  </a>
                </Link>
              </li>
            )}
          </ul>
        </nav>
        <nav className='asideNavigation'>
          <ul className='flex justifyEnd alignCenter'>
            <li className="moreWrap" ref={moreRef}>
              <button
                className={classnames('moreBtn', { active: moreOpen })}
                aria-haspopup="menu"
                aria-expanded={moreOpen}
                onClick={(e) => {
                  e.stopPropagation()
                  setMoreOpen((v) => !v)
                }}
              >
                More <span className="caret" />
              </button>

              {moreOpen && (
                <div className="moreMenu" role="menu">
                  <Link legacyBehavior href={ROUTES.Membership}>
                    <a role="menuitem" onClick={() => setMoreOpen(false)}>Memberships</a>
                  </Link>
                  <Link legacyBehavior href={ROUTES.About}>
                    <a role="menuitem" onClick={() => setMoreOpen(false)}>About</a>
                  </Link>
                  <Link legacyBehavior href={ROUTES.Contact}>
                    <a role="menuitem" onClick={() => setMoreOpen(false)}>Contact</a>
                  </Link>
                  <Link legacyBehavior href={ROUTES.NeedHelp}>
                    <a role="menuitem" onClick={() => setMoreOpen(false)}>Help</a>
                  </Link>
                </div>
              )}
            </li>
            <li className="navMembership">
              <Link legacyBehavior href={ROUTES.Membership}>
                <a>Memberships</a>
              </Link>
            </li>
            <li className="navAbout">
              <Link legacyBehavior href={ROUTES.About}>
                <a>About</a>
              </Link>
            </li>
            <li className="navContact">
              <Link legacyBehavior href={ROUTES.Contact}>
                <a>Contact</a>
              </Link>
            </li>
            <li className="navHelp">
              <Link legacyBehavior href={ROUTES.NeedHelp}>
                <a>Help</a>
              </Link>
            </li>
            {!isAuthenticated && (
              <li>
                <Link legacyBehavior href={ROUTES.Dealers}>
                  <a className='dealerSignUp'>Dealer Sign Up</a>
                </Link>
              </li>
            )}
            <li>
              {isAuthenticated ? (
                <>
                  <Desktop>
                    <UserMenu />
                  </Desktop>
                  <Mobile>
                    <button className='flatBtn' onClick={logout}>
                      Sign Out
                    </button>
                  </Mobile>
                </>
              ) : (
                <>
                  {/* // for home page */}
                  <Desktop>
                    <span className="headerSignInWrap">
                      <Button
                        onClick={() => {
                          setAuthModal({ open: true, view: VIEWS.Login })
                          setOpen(false)
                        }}
                      >
                        Sign In
                      </Button>
                    </span>
                  </Desktop>
                  <Mobile>
                    <button
                      className='flatBtn primary'
                      onClick={() => {
                        setAuthModal({ open: true, view: VIEWS.Login })
                        setOpen(false)
                      }}
                    >
                      Sign In
                    </button>
                  </Mobile>
                </>
              )}
            </li>
          </ul>
        </nav>

        {notifications[NotificationType.Message]?.length > 0 && (
          <div className='chat' onClick={handleMessageClick}>
            <Message fill='var(--alert-color)' count={chatCount} />
          </div>
        )}
        <button
          aria-label={open ? 'close menu' : 'open menu'}
          className='flatBtn burgerBtn'
          onClick={() => setOpen(!open)}
        >
          {open ? <FlatX /> : <Hamburger />}
        </button>
      </div>

      <style jsx>{`
        header {
          position: sticky;
          top: 0;
          height: 100vh;
          max-height: 8.5rem;
          background: var(--secondary-bg-color);
          transition: max-height 0.15s;
          box-shadow: 0 0.8rem 0.8rem rgba(0, 0, 0, 0.06);

          &.open {
            max-height: 100vh;

            .burgerBtn {
              width: 3.6rem;
              height: 3.6rem;
              margin: 0 0.4rem;
            }
          }
        }
        .chat {
          display: none;
          position: absolute;
          right: 5rem;
          top: 1.3rem;
          cursor: pointer;
        }

        .burgerBtn {
          display: none;
          position: absolute;
          top: 0.9rem;
          right: 0.2rem;
          width: 4.2rem;
          height: 3.6rem;
          padding: 1rem;

          :global(svg) {
            width: 100%;
            height: 100%;
          }
        }

        .container {
          width: 100%;
          height: 100%;
          margin: 0 auto;
          padding: 0 4.2rem;
          max-width: 192rem;
        }

        .logoContainer {
          max-width: 21rem;
          width: 100%;
          margin-right: 5.6rem;
        }

        .mainNavigation {
          flex: 1;

          li:not(:last-child) {
            margin-right: 3rem;
          }

          a {
            font-weight: bold;
            font-size: 1.8rem;
            color: #fff;

            &.primary {
              font-weight: 700;
              color: var(--text-link-color);
            }
          }
        }

        .asideNavigation {
          li:not(:last-child) {
            margin-right: 3rem;
          }

          a,
          button {
            font-weight: 400;
            font-size: 1.6rem;
            color: #fff;

            &.primary {
              font-weight: 700;
              color: var(--text-link-color);
            }
            
            &.dealerSignUp {
              padding: 1rem 4.2rem;
              border-radius: 4.1rem;
              font-size: 1.8rem;
              border: 1px solid #47648f;
              font-weight: bold;
              transition: all 0.15s ease-in-out;
            }

            &.dealerSignUp:hover {
              background: rgba(255, 255, 255, 0.1);
            }

            &.dealerSignUp:focus {
              background: rgba(255, 255, 255, 0.07);
            }
          }
        }

        @media (max-width: 1200px) {
          .container {
            padding: 0 3rem;
          }
          .mainNavigation {
            li:not(:last-child) {
              margin-right: 2rem;
            }

            a {
              font-size: 1.7rem;
            }
          }

          .asideNavigation {
            li:not(:last-child) {
              margin-right: 2rem;
            }

            a,
            button {
              font-size: 1.5rem;
            }
          }
        }

        @media (max-width: 1024px) {
          .chat {
            display: inline;
          }
          header {
            max-height: 5.5rem;
            overflow: hidden;
            box-shadow: 0 0.8rem 0.8rem rgba(0, 0, 0, 0.06);

            .burgerBtn {
              display: block;
            }

            .container {
              display: block;
              padding: 0;
              background: var(--secondary-bg-color);

              .logoContainer {
                position: sticky;
                top: 0;
                max-width: 100%;
                height: 5.5rem;
                padding: 1.9rem 1.2rem 0;
                background: var(--secondary-bg-color);
                box-shadow: 0 0.8rem 0.8rem rgba(0, 0, 0, 0.06);
              }

              .asideNavigation,
              .mainNavigation {
                max-width: 100%;

                :global(ul) {
                  display: block;

                  li {
                    margin: 0;
                    border-bottom: 1px solid #47648f;

                    a {
                      display: block;
                    }

                    a,
                    button {
                      padding: 2rem 1.2rem;
                      font-size: 1.4rem;
                      font-weight: 700;

                      &.dealerSignUp {
                        border: none;
                      }
                    }
                  }
                }

                .primary {
                  width: 100%;
                  text-align: left;
                }
              }

              .asideNavigation {
                :global(ul) {
                  li {
                    &:last-child {
                      border-bottom: none;
                    }

                    :global(& > .wrapper) {
                      width: fit-content;

                      :global(.profile) {
                        margin-top: 5rem;
                      }
                    }
                  }
                }
              }

              .notifications {
                display: inline-flex;
                margin: 0 0 0 1.3rem;
                width: 2rem;
                height: 2rem;
                background: var(--alert-color);
                border-radius: 50%;
                color: #fff;
              }
            }
          }
        }
      `}</style>

      <style jsx>{`
        header {
          z-index: ${zIndex.Layout};
        }

        @media (max-width: 1024px) {
          header {
            z-index: ${zIndex.Modal + 1};
          }
        }
      `}</style>

      {/* for home page */}
      <style jsx>{`
        .moreWrap {
          display: none;
          position: relative;
        }

        .moreBtn {
          display: inline-flex;
          align-items: center;
          gap: 0.8rem;
          height: 4.2rem;
          padding: 0 1.6rem;
          border-radius: 999px;
          background: transparent;
          font-weight: 700;
          cursor: pointer;
          white-space: nowrap;
        }

        .moreBtn:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .caret {
          width: 0;
          height: 0;
          border-left: 0.45rem solid transparent;
          border-right: 0.45rem solid transparent;
          border-top: 0.55rem solid rgba(255, 255, 255, 0.85);
          transform: translateY(1px);
        }

        .moreMenu {
          position: absolute;
          top: calc(100% + 0.9rem);
          right: 0;
          min-width: 22rem;
          padding: 0.8rem;
          border-radius: 1rem;
          background: var(--secondary-bg-color);
          border: 1px solid rgba(255, 255, 255, 0.12);
          box-shadow: 0 1.2rem 3rem rgba(0, 0, 0, 0.45);
        }

        .moreMenu :global(a) {
          display: block;
          padding: 1.1rem 1.2rem;
          border-radius: 0.9rem;
          font-weight: 700;
          color: rgba(255, 255, 255, 0.9);
        }

        .moreMenu :global(a:hover) {
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
        }

        @media (max-width: 1400px) and (min-width: 1025px) {
          .moreWrap {
            display: inline-flex;
            align-items: center;
          }

          .asideNavigation .navMembership,
          .asideNavigation .navAbout,
          .asideNavigation .navContact,
          .asideNavigation .navHelp {
            display: none;
          }
        }
          
        @media (min-width: 1024px) {
          .headerSignInWrap :global(button) {
            border-radius: 8px;
          }

          header.homeHeader {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 9999;
            max-height: 11rem;
            height: 11rem;
            background: transparent;
            box-shadow: none;
            transition: all 0.2s ease-in-out;
          }

          header.homeHeader.scrolled {
            max-height: 8.5rem;
            height: 8.5rem;
            background: var(--secondary-bg-color);
            box-shadow: 0 0.8rem 0.8rem rgba(0, 0, 0, 0.06);
          }
        }
      `}</style>
    </header>
  )
})

export default Header
